import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Notification from '@/components/Notification';
import { 
  Users, 
  UserPlus, 
  Briefcase, 
  DollarSign, 
  CreditCard, 
  Calendar,
  TrendingUp,
  FileText,
  CheckCircle,
  ChevronRight,
  Activity,
} from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useAuthStore } from '../stores/authStore';
import { PERMISSIONS } from '../types/permissions';
import { cn } from '@/lib/utils';
import { ScrollArea } from "@/components/ui/scroll-area";
import HRDashboardExtra from './HRDashboardExtra';
import { useHiringData } from '../hooks/useHiringData';
import { useEmployeeData } from '../hooks/useEmployeeData';
import AgreementNotifications from '../components/AgreementNotifications';
import CompensationNotifications from '../components/CompensationNotifications';

// --- Centralized Mock Data for Dynamic Content ---
const MOCK_MODULE_DATA = [
  {
    title: 'Employee Management',
    icon: <Users className="h-5 w-5" />,
    color: 'bg-blue-500',
    permissions: [PERMISSIONS.EMPLOYEES_VIEW, PERMISSIONS.DEPARTMENT_VIEW],
    stats: [
      { label: 'Total Employees', value: '0', change: '0', trend: 'neutral', href: '/employees', permission: PERMISSIONS.EMPLOYEES_VIEW, isRealData: true },
      { label: 'Active Projects', value: '0', change: '0', trend: 'neutral', href: '/projects', permission: PERMISSIONS.EMPLOYEES_VIEW, isRealData: false },
      { label: 'Attendance Rate', value: '0%', change: '0', trend: 'neutral', href: '/employees/attendance', permission: PERMISSIONS.EMPLOYEES_VIEW, isRealData: true }
    ]
  },
  {
    title: 'Hiring Management',
    icon: <UserPlus className="h-5 w-5" />,
    color: 'bg-green-500',
    permissions: [PERMISSIONS.JOB_VIEW, PERMISSIONS.CANDIDATE_VIEW],
    stats: [
      { label: 'Open Positions', value: '0', change: '0', trend: 'neutral', href: '/hiring/jobs', permission: PERMISSIONS.JOB_VIEW, isRealData: true },
      { label: 'Active Candidates', value: '0', change: '0', trend: 'neutral', href: '/hiring/candidates', permission: PERMISSIONS.CANDIDATE_VIEW, isRealData: true },
      { label: 'Interviews This Week', value: '0', change: '0', trend: 'neutral', href: '/hiring/candidates', permission: PERMISSIONS.JOB_VIEW, isRealData: true }
    ]
  },
  {
    title: 'Project Management',
    icon: <Briefcase className="h-5 w-5" />,
    color: 'bg-purple-500',
    permissions: [PERMISSIONS.EMPLOYEES_VIEW],
    stats: [
      { label: 'Active Projects', value: '15', change: '+3', trend: 'up', href: '/projects', permission: PERMISSIONS.EMPLOYEES_VIEW, isRealData: false },
      { label: 'Completed Projects', value: '8', change: '+2', trend: 'up', href: '/projects', permission: PERMISSIONS.EMPLOYEES_VIEW, isRealData: false },
      { label: 'Project Health', value: '92%', change: '+5%', trend: 'up', href: '/projects', permission: PERMISSIONS.EMPLOYEES_VIEW, isRealData: false }
    ]
  },
  {
    title: 'Sales & Clients',
    icon: <DollarSign className="h-5 w-5" />,
    color: 'bg-orange-500',
    permissions: [PERMISSIONS.EMPLOYEES_VIEW],
    stats: [
      { label: 'Total Clients', value: '32', change: '+4', trend: 'up', href: '/sales/clients', permission: PERMISSIONS.EMPLOYEES_VIEW, isRealData: false },
      { label: 'Monthly Revenue', value: '$45K', change: '+15%', trend: 'up', href: '/sales/revenue', permission: PERMISSIONS.EMPLOYEES_VIEW, isRealData: false },
      { label: 'Growth Rate', value: '+15%', change: '+3%', trend: 'up', href: '/sales/revenue', permission: PERMISSIONS.EMPLOYEES_VIEW, isRealData: false }
    ]
  },
  {
    title: 'Finance',
    icon: <CreditCard className="h-5 w-5" />,
    color: 'bg-red-500',
    permissions: [PERMISSIONS.EMPLOYEES_VIEW],
    stats: [
      { label: 'Pending Invoices', value: '12', change: '-3', trend: 'down', href: '/finance', permission: PERMISSIONS.EMPLOYEES_VIEW, isRealData: false },
      { label: 'Monthly Expenses', value: '$28K', change: '-5%', trend: 'down', href: '/finance', permission: PERMISSIONS.EMPLOYEES_VIEW, isRealData: false },
      { label: 'Profit Margin', value: '35%', change: '+2%', trend: 'up', href: '/finance', permission: PERMISSIONS.EMPLOYEES_VIEW, isRealData: false }
    ]
  }
];

const MOCK_ACTIVITIES_DATA = [
  {
    id: 1,
    type: 'employee',
    action: 'New Employee Onboarded',
    details: 'Sarah Johnson joined as Senior Developer',
    time: '2 hours ago',
    icon: <Users className="h-4 w-4" />,
    status: 'success',
    href: '/employees',
    permission: PERMISSIONS.EMPLOYEES_VIEW
  },
  {
    id: 2,
    type: 'project',
    action: 'Project Milestone Completed',
    details: 'E-commerce Platform Phase 1 delivered',
    time: '5 hours ago',
    icon: <CheckCircle className="h-4 w-4" />,
    status: 'success',
    href: '/projects',
    permission: PERMISSIONS.EMPLOYEES_VIEW
  },
  {
    id: 3,
    type: 'sales',
    action: 'New Contract Signed',
    details: 'Enterprise deal with TechCorp Inc.',
    time: '1 day ago',
    icon: <FileText className="h-4 w-4" />,
    status: 'success',
    href: '/sales/contracts',
    permission: PERMISSIONS.EMPLOYEES_VIEW
  },
  {
    id: 4,
    type: 'finance',
    action: 'Invoice Generated',
    details: 'Invoice #INV-2024-001 for $15,000',
    time: '1 day ago',
    icon: <CreditCard className="h-4 w-4" />,
    status: 'pending',
    href: '/finance/invoices',
    permission: PERMISSIONS.EMPLOYEES_VIEW
  }
];

const MOCK_EVENTS_DATA = [
  {
    id: 1,
    title: 'Team Meeting',
    time: 'Today at 2:00 PM',
    type: 'meeting',
    icon: <Calendar className="h-4 w-4" />,
    priority: 'high',
    href: '/team-meetings',
    permission: PERMISSIONS.EMPLOYEES_VIEW
  },
  {
    id: 2,
    title: 'Client Presentation',
    time: 'Tomorrow at 10:00 AM',
    type: 'presentation',
    icon: <FileText className="h-4 w-4" />,
    priority: 'medium',
    href: '/client-presentations',
    permission: PERMISSIONS.EMPLOYEES_VIEW
  },
  {
    id: 3,
    title: 'Project Review',
    time: 'Tomorrow at 3:00 PM',
    type: 'review',
    icon: <CheckCircle className="h-4 w-4" />,
    priority: 'high',
    href: '/project-reviews',
    permission: PERMISSIONS.EMPLOYEES_VIEW
  }
];

const MOCK_METRICS_DATA = [
  {
    title: 'Project Completion Rate',
    value: 85,
    target: 90,
    icon: <TrendingUp className="h-4 w-4" />,
    color: 'bg-blue-500',
    permission: PERMISSIONS.EMPLOYEES_VIEW
  },
  {
    title: 'Client Satisfaction',
    value: 92,
    target: 95,
    icon: <CheckCircle className="h-4 w-4" />,
    color: 'bg-green-500',
    permission: PERMISSIONS.EMPLOYEES_VIEW
  },
  {
    title: 'Resource Utilization',
    value: 78,
    target: 85,
    icon: <Users className="h-4 w-4" />,
    color: 'bg-purple-500',
    permission: PERMISSIONS.EMPLOYEES_VIEW
  }
];

// Module stats with permission checks
const getModuleStats = (hasPermission: (permission: string) => boolean) => {
  return MOCK_MODULE_DATA.filter(module =>
    module.permissions.some(p => hasPermission(p))
  ).map(module => ({
    ...module,
    stats: module.stats.filter(stat => hasPermission(stat.permission))
  }));
};

// Recent activities with permission checks
const getRecentActivities = (hasPermission: (permission: string) => boolean) => {
  return MOCK_ACTIVITIES_DATA.filter(activity => hasPermission(activity.permission));
};

// Upcoming events with permission checks
const getUpcomingEvents = (hasPermission: (permission: string) => boolean) => {
  return MOCK_EVENTS_DATA.filter(event => hasPermission(event.permission));
};

// Performance metrics with permission checks
const getPerformanceMetrics = (hasPermission: (permission: string) => boolean) => {
  return MOCK_METRICS_DATA.filter(metric => hasPermission(metric.permission));
};

// Add a function to get the time-based greeting
function getTimeGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

// Motivational quotes/tips
const MOTIVATIONAL_QUOTES = [
  'Success is not the key to happiness. Happiness is the key to success.',
  'The only way to do great work is to love what you do.',
  'Stay positive, work hard, make it happen.',
  "Your limitation—it's only your imagination.",
  'Push yourself, because no one else is going to do it for you.'
];
function getRandomQuote() {
  return MOTIVATIONAL_QUOTES[Math.floor(Math.random() * MOTIVATIONAL_QUOTES.length)];
}

// Animated statistics hook
function useCountUp(target: number, duration = 1200) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const increment = target / (duration / 16);
    function step() {
      start += increment;
      if (start < target) {
        setCount(Math.floor(start));
        requestAnimationFrame(step);
      } else {
        setCount(target);
      }
    }
    step();
    // eslint-disable-next-line
  }, [target]);
  return count;
}

const Dashboard: React.FC = () => {
  const { hasPermission, user } = useAuthStore();
  const { stats: hiringStats } = useHiringData();
  const { stats: employeeStats } = useEmployeeData();

  const moduleStats = getModuleStats(hasPermission);
  
  // Update hiring management stats with real data
  const updatedModuleStats = moduleStats.map(module => {
    if (module.title === 'Hiring Management') {
      return {
        ...module,
        stats: module.stats.map(stat => {
          if (stat.isRealData) {
            let value = '0';
            let change = '0';
            let trend: 'up' | 'down' | 'neutral' = 'neutral';
            
            if (stat.label === 'Open Positions') {
              value = hiringStats.openPositions.toString();
            } else if (stat.label === 'Active Candidates') {
              value = hiringStats.activeCandidates.toString();
            } else if (stat.label === 'Interviews This Week') {
              value = hiringStats.interviewsThisWeek.toString();
            }
            
            return { ...stat, value, change, trend };
          }
          return stat;
        })
      };
    } else if (module.title === 'Employee Management') {
      return {
        ...module,
        stats: module.stats.map(stat => {
          if (stat.isRealData) {
            let value = '0';
            let change = '0';
            let trend: 'up' | 'down' | 'neutral' = 'neutral';
            
            if (stat.label === 'Total Employees') {
              value = employeeStats.totalEmployees.toString();
            } else if (stat.label === 'Attendance Rate') {
              value = `${employeeStats.averageAttendance}%`;
            }
            
            return { ...stat, value, change, trend };
          }
          return stat;
        })
      };
    }
    return module;
  });

  const recentActivities = getRecentActivities(hasPermission);
  const upcomingEvents = getUpcomingEvents(hasPermission);
  const performanceMetrics = getPerformanceMetrics(hasPermission);

  const userName = user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.email || 'User';

  const [quote] = useState(getRandomQuote());
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef(null);

  // Accessibility: close notifications on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (notifRef.current && (notifRef.current as any).contains(e.target)) return;
      setNotifOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // If user is HR Manager, show only HR dashboard
  if (user?.role === 'HR Manager') {
    return (
      <div className="min-h-screen p-4 sm:p-6 space-y-4 sm:space-y-6">
        <Notification
          open={notifOpen}
          message="Welcome to your HR Management Dashboard!"
          severity="info"
          onClose={() => setNotifOpen(false)}
        />
        
        {/* Enhanced Greeting Section for HR */}
        <div className="via-indigo-50 to-purple-50 rounded-xl shadow-lg border border-blue-100 p-4 sm:p-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 sm:gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                    {getTimeGreeting()}, {userName}
                  </h1>
                  <p className="text-sm text-gray-500 mt-1">
                    {new Date().toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                
                </div>
              </div>
              <p className="text-gray-700 italic text-sm leading-relaxed">
                "{quote}"
              </p>
            </div>
          </div>
        </div>

        {/* Only HR Dashboard Content */}
        <HRDashboardExtra />
      </div>
    );
  }

  // Regular dashboard for non-HR users
  return (
    <div className="min-h-screen p-4 sm:p-6 space-y-4 sm:space-y-6">
      <Notification
        open={notifOpen}
        message="Welcome to your dashboard!"
        severity="info"
        onClose={() => setNotifOpen(false)}
      />
      
      {/* Enhanced Greeting Section */}
      <div className="via-indigo-50 to-purple-50 rounded-xl shadow-lg border border-blue-100 p-4 sm:p-8">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 sm:gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-4">
              
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                  {getTimeGreeting()}, {userName}
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  {new Date().toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
            </div>
           
              <p className="text-gray-700 italic text-sm leading-relaxed">
                "{quote}"
              </p>
           
          </div>
        </div>
      </div>
      
      {/* Module Stats Grid: Simplified */}
      <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-2">
        {updatedModuleStats.map((module) => (
          <Link
            key={module.title}
            to={module.stats[0]?.href || '/'}
            className="block"
          >
            <Card className="h-full hover:shadow-md transition-shadow">
              <CardHeader className="pb-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0">
                  <div className="flex items-center gap-3">
                    <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${module.color} text-white`}>
                      {module.icon}
                    </div>
                    <CardTitle className="text-base sm:text-lg font-semibold">
                        {module.title}
                      </CardTitle>
                  </div>
                  {module.title === 'Employee Management' && (
                    <div className="flex items-center gap-2">
                      
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4">
                  {module.stats.map((stat) => (
                    hasPermission(stat.permission) && (
                      <div key={stat.label} className="text-center">
                        <div className="text-xl sm:text-2xl font-bold text-gray-900">
                            {useCountUp(Number(stat.value.replace(/[^0-9]/g, '')) || 0)}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-600">{stat.label}</div>

                       
                      </div>
                    )
                  ))}
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Notifications Grid for Super Admin */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <AgreementNotifications />
        <CompensationNotifications />
      </div>

      {/* Performance Metrics: Simplified */}
      {performanceMetrics.length > 0 && (
        <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {performanceMetrics.map((metric) => (
            <Card key={metric.title} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold">
                  {metric.title}
                </CardTitle>
                  <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${metric.color} text-white`}>
                  {metric.icon}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Current: <span className="font-semibold">{metric.value}%</span></span>
                    <span className="text-gray-600">Target: <span className="font-semibold">{metric.target}%</span></span>
                  </div>
                  <Progress
                    value={(metric.value / metric.target) * 100}
                    className="h-2"
                  />
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Performance</span>
                    <Badge variant={metric.value >= metric.target ? "default" : "secondary"}>
                      {metric.value >= metric.target ? 'On Track' : 'Needs Attention'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="grid gap-4 sm:gap-6 grid-cols-1 lg:grid-cols-2">
        {/* Recent Activities: Simplified */}
        {recentActivities.length > 0 && (
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-blue-600" />
                  <CardTitle className="text-lg font-semibold">Recent Activities</CardTitle>
                  </div>
                <Button variant="ghost" size="sm">
                  View All <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px]">
                <div className="space-y-4">
                    {recentActivities.map((activity) => (
                    <Link
                        key={activity.id}
                          to={activity.href}
                      className="flex items-center gap-4 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <div className={cn(
                        "p-2 rounded-lg",
                            activity.status === 'success'
                          ? 'bg-green-100 text-green-600'
                          : 'bg-yellow-100 text-yellow-600'
                          )}>
                            {activity.icon}
                          </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{activity.action}</p>
                        <p className="text-sm text-gray-600">{activity.details}</p>
                      </div>
                      <span className="text-sm text-gray-500">{activity.time}</span>
                    </Link>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
        )}

        {/* Upcoming Events: Simplified */}
        {upcomingEvents.length > 0 && (
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-purple-600" />
                  <CardTitle className="text-lg font-semibold">Upcoming Events</CardTitle>
                  </div>
                <Button variant="ghost" size="sm">
                  View Calendar <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px]">
                <div className="space-y-4">
                    {upcomingEvents.map((event) => (
                    <Link
                        key={event.id}
                          to={event.href}
                      className="flex items-center gap-4 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <div className={cn(
                        "p-2 rounded-lg",
                            event.priority === 'high'
                          ? 'bg-red-100 text-red-600'
                          : 'bg-blue-100 text-blue-600'
                          )}>
                            {event.icon}
                          </div>
                          <div className="flex-1">
                        <p className="font-medium text-gray-900">{event.title}</p>
                        <p className="text-sm text-gray-600">{event.time}</p>
                          </div>
                      <Button variant="outline" size="sm">
                            View
                          </Button>
                        </Link>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
        )}
      </div>
    </div>
  );
};

export default Dashboard; 